import type {
  Account,
  EventType as UserEventType,
  EventLookupByType as UserEventLookupByType,
  SubEntryType,
  SubEntryLookupByType,
} from "@tandemdiabetes/sugarmate-api-react-client"
import { useMemo } from "react"
import { useFindUserEntries } from "src/hooks/userEntry"
import { useFindUserEvents } from "src/hooks/userEvent"
import type {
  UserItemQuerySelector,
  UserItemQuerySort,
  UserItemSharedProperties,
} from "src/types"
import {
  createEntryQueryObjectForType,
  createEventQueryObjectForType,
  userEntriesToSubEntryUserItems,
} from "src/utils"

type ReturnValue<A extends SubEntryType & UserEventType> = {
  isFetchingEvents: boolean;
  isFetchingEntries: boolean;
  events: Array<UserEventLookupByType<A>>;
  subEntries: Array<SubEntryLookupByType<A> & UserItemSharedProperties>;
}

export function useEventsAndSubEntriesOfType<A extends UserEventType & SubEntryType>(
  accountId: Account["id"],
  activityType: A,
  selector: UserItemQuerySelector,
  limit?: number,
  sort?: UserItemQuerySort,
): ReturnValue<A> {
  const eventQueryObject = useMemo(
    () => createEventQueryObjectForType(
      accountId,
      activityType,
      {
        selector,
        sort,
        limit,
      },
    ),
    [
      accountId,
      activityType,
      selector,
      sort,
      limit,
    ],
  )

  const entryQueryObject = useMemo(
    () => createEntryQueryObjectForType(
      accountId,
      activityType,
      {
        selector,
        sort,
        limit,
      },
    ),
    [
      accountId,
      activityType,
      selector,
      sort,
      limit,
    ],
  )

  const {
    result: events,
    isFetching: isFetchingEvents,
  } = useFindUserEvents<UserEventLookupByType<A>>(eventQueryObject)

  const {
    result: entries,
    isFetching: isFetchingEntries,
  } = useFindUserEntries(entryQueryObject)

  const subEntries = useMemo(
    () => userEntriesToSubEntryUserItems(
      entries,
      activityType,
    ),
    [
      entries,
      activityType,
    ],
  )

  return {
    isFetchingEvents,
    isFetchingEntries,
    events: events as Array<UserEventLookupByType<A>>,
    subEntries,
  }
}
